import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { seekerDataService } from '../../services/seekerDataService';
import { getSupabaseClient } from '../../lib/supabase';
import { SeekerProfile } from '../../types';

type TabKey = 'home' | 'profile';

const tabDefinitions: Array<{
  key: TabKey;
  label: string;
  icon: React.ReactNode;
  path: string;
}> = [
  {
    key: 'home',
    label: 'Home',
    path: '/home',
    icon: (
      <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 20 20">
        <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" />
      </svg>
    )
  },
  {
    key: 'profile',
    label: 'Profile',
    path: '/home/profile',
    icon: (
      <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
      </svg>
    )
  }
];

const formatPhoneNumber = (phone: string): string => {
  // Remove all non-digit characters
  const digits = phone.replace(/\D/g, '');

  // Handle 10-digit US numbers (add +1)
  if (digits.length === 10) {
    return `+1 (${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }

  // Handle 11-digit numbers starting with 1
  if (digits.length === 11 && digits.startsWith('1')) {
    return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }

  // For other formats, return as-is
  return phone;
};

export const SeekerProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [profile, setProfile] = useState<SeekerProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const activeTab: TabKey = 'profile';

  const handleTabClick = (path: string) => {
    if (location.pathname === path) return;
    navigate(path);
  };

  useEffect(() => {
    const loadProfile = async () => {
      try {
        setLoading(true);
        const { profile } = await seekerDataService.getCurrentUser();
        setProfile(profile);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load profile');
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, []);

  const handleSignOut = async () => {
    try {
      const supabase = getSupabaseClient();
      const { error } = await supabase.auth.signOut();

      if (error) {
        console.error('Sign out error:', error);
        alert('Failed to sign out. Please try again.');
      } else {
        // Clear all cached data to prevent leaking across sessions
        localStorage.clear();
        sessionStorage.clear();
        // Redirect to landing page after successful sign out
        navigate('/', { replace: true });
      }
    } catch (err) {
      console.error('Sign out error:', err);
      alert('Failed to sign out. Please try again.');
    }
  };

  const handleBackToHome = () => {
    navigate('/home');
  };

  const handleEditProfile = () => {
    navigate('/profile/edit');
  };

  const handleViewTransactions = () => {
    navigate('/home/transactions');
  };

  const handleRequestNewMatch = () => {
    navigate('/home/request-new-match');
  };

  const handleNotificationSettings = () => {
    navigate('/profile/notifications');
  };

  if (loading) {
    return (
      <div className="flex h-screen min-h-screen w-full items-stretch overflow-hidden">
        <div className="relative flex h-full w-full flex-1 flex-col overflow-hidden bg-[#FFFCEF]">
          <header className="flex h-16 items-center border-b border-[#FFFCEF]/25 bg-[#2C2731] px-4">
            <div className="w-10" aria-hidden />
            <div className="flex flex-1 justify-center">
              <img src="/gclogo.webp" alt="Guides Collective" className="h-8 w-auto" />
            </div>
            <div className="w-10" aria-hidden />
          </header>
          <main className="flex flex-1 min-h-0 items-center justify-center">
            <div className="text-center">
              <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-b-2 border-[#2C2731]" />
              <p className="text-[#2C2731]/70">Loading profile...</p>
            </div>
          </main>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-screen min-h-screen w-full items-stretch overflow-hidden">
        <div className="relative flex h-full w-full flex-1 flex-col overflow-hidden bg-[#FFFCEF]">
          <header className="flex h-16 items-center border-b border-[#FFFCEF]/25 bg-[#2C2731] px-4">
            <div className="w-10" aria-hidden />
            <div className="flex flex-1 justify-center">
              <img src="/gclogo.webp" alt="Guides Collective" className="h-8 w-auto" />
            </div>
            <div className="w-10" aria-hidden />
          </header>
          <main className="flex flex-1 min-h-0 items-center justify-center px-4">
            <div className="max-w-sm text-center">
              <p className="mb-4 text-sm text-red-600">Error: {error}</p>
              <button
                onClick={() => window.location.reload()}
                className="inline-flex items-center justify-center rounded-full border border-[#304031] bg-[#304031] px-6 py-3 text-sm font-semibold text-[#FFFCEF] shadow-[0_16px_36px_-20px_rgba(48,64,49,0.55)] transition-transform hover:-translate-y-0.5 hover:bg-[#1F1924] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FFFCEF] focus-visible:ring-offset-2"
              >
                Retry
              </button>
            </div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen min-h-screen w-full items-stretch overflow-hidden">
      <div className="relative flex h-full w-full flex-1 flex-col overflow-hidden bg-[#FFFCEF]">
        <header className="flex h-16 items-center border-b border-[#FFFCEF]/25 bg-[#2C2731] px-4">
          <button
            type="button"
            className="inline-flex h-10 w-10 items-center justify-center rounded-2xl border border-[#FFFCEF]/30 bg-transparent text-xl text-[#FFFCEF] transition hover:bg-[#FFFCEF]/10"
            onClick={handleBackToHome}
            aria-label="Go back"
          >
            {'\u2190'}
          </button>
          <div className="flex flex-1 justify-center">
            <img src="/gclogo.webp" alt="Guides Collective" className="h-8 w-auto" />
          </div>
          <div className="w-10" aria-hidden />
        </header>

        <main className="flex flex-1 min-h-0 overflow-y-auto">
          <div className="mx-auto w-full max-w-2xl px-6 py-6">
        {/* Profile Header */}
        <div className="mb-6 rounded-2xl border border-[#2C2731] bg-[#FFFCEF] p-6 text-center shadow-sm">
          <div className="relative mb-4 inline-block">
            <div className="mx-auto flex h-20 w-20 items-center justify-center overflow-hidden rounded-full bg-[#E8DFBD]">
              {profile?.imageUrl ? (
                <img src={profile.imageUrl} alt="Profile" className="h-full w-full object-cover" />
              ) : (
                <svg className="h-12 w-12 text-[#2C2731]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              )}
            </div>
            {/* Verification badge */}
            <div className="absolute -bottom-1 -right-1 flex h-6 w-6 items-center justify-center rounded-full border-2 border-[#FFFCEF] bg-[#2C2731]">
              <span className="text-xs font-bold text-[#FFFCEF]">✓</span>
            </div>
          </div>

          <h1 className="mb-1 text-xl font-semibold text-[#2C2731] ph-mask">
            {profile?.name || 'User Name'}
          </h1>
          <p className="text-sm text-[#2C2731]/80 ph-mask">
            {profile?.email || 'user@email.com'}
          </p>
          {profile?.phone && (
            <p className="mt-2 text-sm text-[#2C2731]/60 ph-mask">
              {formatPhoneNumber(profile.phone)}
            </p>
          )}
        </div>

        {/* Profile Actions */}
        <div className="mb-6 rounded-2xl border border-[#2C2731] bg-[#FFFCEF] shadow-sm overflow-hidden">
          <button
            type="button"
            className="flex w-full items-center justify-between border-b border-[#2C2731]/20 p-4 transition hover:bg-[#E8DFBD]"
            onClick={handleEditProfile}
          >
            <div className="flex items-center space-x-3">
              <svg className="h-5 w-5 text-[#2C2731]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              <span className="font-medium text-[#2C2731]">Edit Profile & Preferences</span>
            </div>
            <svg className="h-5 w-5 text-[#2C2731]/50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>

          <button
            type="button"
            className="flex w-full items-center justify-between border-b border-[#2C2731]/20 p-4 transition hover:bg-[#E8DFBD]"
            onClick={handleViewTransactions}
          >
            <div className="flex items-center space-x-3">
              <svg className="h-5 w-5 text-[#2C2731]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              <span className="font-medium text-[#2C2731]">View Transactions</span>
            </div>
            <svg className="h-5 w-5 text-[#2C2731]/50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>

          <button className="flex w-full items-center justify-between p-4 transition hover:bg-[#E8DFBD]" onClick={handleRequestNewMatch}>
            <div className="flex items-center space-x-3">
              <svg className="h-5 w-5 text-[#2C2731]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192L5.636 18.364M12 12h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="font-medium text-[#2C2731]">Request a Different Guide</span>
            </div>
            <svg className="h-5 w-5 text-[#2C2731]/50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>

        {/* Account Settings */}
        <div className="mb-6">
          <h2 className="mb-3 text-lg font-semibold text-[#2C2731]">Account Settings</h2>
          <div className="rounded-2xl border border-[#2C2731] bg-[#FFFCEF] shadow-sm overflow-hidden">
            <button
              className="flex w-full items-center justify-between border-b border-[#2C2731]/20 p-4 transition hover:bg-[#E8DFBD]"
              onClick={handleNotificationSettings}
            >
              <div className="flex items-center space-x-3">
                <svg className="h-5 w-5 text-[#2C2731]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-5 5V12h-5l5-5v10z" />
                </svg>
                <span className="font-medium text-[#2C2731]">Notifications</span>
              </div>
              <svg className="h-5 w-5 text-[#2C2731]/50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>

            {/*<button className="flex w-full items-center justify-between border-b border-[#2C2731]/20 p-4 transition hover:bg-[#E8DFBD]">
              <div className="flex items-center space-x-3">
                <svg className="h-5 w-5 text-[#2C2731]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                <span className="font-medium text-[#2C2731]">Privacy & Security</span>
              </div>
              <svg className="h-5 w-5 text-[#2C2731]/50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>*/}

            <button
              onClick={() => navigate('/payment-methods')}
              className="flex w-full items-center justify-between border-b border-[#2C2731]/20 p-4 transition hover:bg-[#E8DFBD]"
            >
              <div className="flex items-center space-x-3">
                <svg className="h-5 w-5 text-[#2C2731]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                </svg>
                <span className="font-medium text-[#2C2731]">Payment Methods</span>
              </div>
              <svg className="h-5 w-5 text-[#2C2731]/50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>

            {/*<button className="flex w-full items-center justify-between p-4 transition hover:bg-[#E8DFBD]">
              <div className="flex items-center space-x-3">
                <svg className="h-5 w-5 text-[#2C2731]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
                </svg>
                <span className="font-medium text-[#2C2731]">Language</span>
              </div>
              <div className="flex items-center space-x-2">
                <span className="text-[#2C2731]/70">English</span>
                <svg className="h-5 w-5 text-[#2C2731]/50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </button>*/}
          </div>
        </div>

        {/* App Information */}
        <div className="mb-6">
          <h2 className="mb-3 text-lg font-semibold text-[#2C2731]">App Information</h2>
          <div className="rounded-2xl border border-[#2C2731] bg-[#FFFCEF] shadow-sm overflow-hidden">
            {/*<button className="flex w-full items-center justify-between border-b border-[#2C2731]/20 p-4 transition hover:bg-[#E8DFBD]">
              <div className="flex items-center space-x-3">
                <svg className="h-5 w-5 text-[#2C2731]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="font-medium text-[#2C2731]">Help & FAQ</span>
              </div>
              <svg className="h-5 w-5 text-[#2C2731]/50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>*/}

            <button className="flex w-full items-center justify-between border-b border-[#2C2731]/20 p-4 transition hover:bg-[#E8DFBD]" onClick={() => navigate('/terms/full')}>
              <div className="flex items-center space-x-3">
                <svg className="h-5 w-5 text-[#2C2731]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <span className="font-medium text-[#2C2731]">Terms of Service</span>
              </div>
              <svg className="h-5 w-5 text-[#2C2731]/50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>

            <button className="flex w-full items-center justify-between border-b border-[#2C2731]/20 p-4 transition hover:bg-[#E8DFBD]" onClick={() => navigate('/privacypolicy')}>
              <div className="flex items-center space-x-3">
                <svg className="h-5 w-5 text-[#2C2731]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                <span className="font-medium text-[#2C2731]">Privacy Policy</span>
              </div>
              <svg className="h-5 w-5 text-[#2C2731]/50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>

            <div className="flex items-center justify-between p-4">
              <div className="flex items-center space-x-3">
                <svg className="h-5 w-5 text-[#2C2731]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="font-medium text-[#2C2731]">App Version</span>
              </div>
              <span className="text-[#2C2731]/70">1.0.0</span>
            </div>
          </div>
        </div>

        {/* Sign Out */}
        <div className="rounded-2xl border border-[#2C2731] bg-[#FFFCEF] shadow-sm overflow-hidden">
          <button
            onClick={handleSignOut}
            className="flex w-full items-center justify-center p-4 text-sm font-medium text-red-600 transition hover:bg-[#FBE2E2]"
          >
            <svg className="mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
            Sign Out
          </button>
        </div>
          </div>
        </main>

        <nav className="shrink-0 border-t border-[#FEF6D6]/40 bg-[#304031]">
          <div className="mx-auto max-w-sm px-4 py-2">
            <div className="flex justify-around">
              {tabDefinitions.map((tab) => {
                const isActive = activeTab === tab.key;
                return (
                  <button
                    key={tab.key}
                    type="button"
                    onClick={() => handleTabClick(tab.path)}
                    className={`relative flex flex-col items-center px-3 py-2 transition-colors ${
                      isActive ? 'text-[#FEF6D6]' : 'text-[#FEF6D6]/60'
                    }`}
                  >
                    <span className={`mb-1 ${isActive ? 'text-[#FEF6D6]' : 'text-[#FEF6D6]/60'}`}>
                      {tab.icon}
                    </span>
                    <span
                      className={`text-xs ${
                        isActive ? 'font-medium text-[#FEF6D6]' : 'text-[#FEF6D6]/60'
                      }`}
                    >
                      {tab.label}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </nav>
      </div>
    </div>
  );
};
